import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { MapContainer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import DynamicTileLayer from '@/components/DynamicTileLayer';
import MapSelector from '@/components/MapSelector';
import CategoryFilter from '@/components/passport/CategoryFilter';
import WaypointSheet from '@/components/passport/WaypointSheet';
import VestigiaRanking from '@/components/VestigiaRanking';
import PassportGrid from '@/components/passport/PassportGrid';
import { downloadDiploma, shareAchievement } from '@/lib/downloads';
import { ArrowLeft, Loader2, Search, Stamp, Trophy, Award, Share2, Map as MapIcon, LayoutGrid, WifiOff, CloudUpload } from 'lucide-react';
import usePhotoQueue from '@/hooks/usePhotoQueue';
import { offlineQueue } from '@/lib/offlineQueue';

const markerIcon = (wp, selected, stampUrl) => {
  if (wp.waypoint_type === 'info') {
    return L.divIcon({ className: '', iconSize: [18, 18], iconAnchor: [9, 9],
      html: `<div data-testid="wp-marker-${wp.id}" data-status="info" style="width:18px;height:18px;border-radius:4px;background:#6b7280;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);color:#fff;font-size:11px;font-weight:800;display:flex;align-items:center;justify-content:center">P</div>` });
  }
  if (wp.status === 'verified' && stampUrl) {
    const s = selected ? 46 : 38;
    return L.divIcon({ className: '', iconSize: [s, s], iconAnchor: [s / 2, s / 2],
      html: `<img data-testid="wp-marker-${wp.id}" data-status="verified" src="${stampUrl}" style="width:${s}px;height:${s}px;object-fit:contain;filter:drop-shadow(0 2px 4px rgba(0,0,0,.45))" />` });
  }
  const ring = wp.status === 'verified' ? '#16a34a' : wp.status === 'pending' ? '#f59e0b' : wp.status === 'queued' ? '#2563eb' : '#ffffff';
  const size = selected ? 34 : 26;
  const mark = wp.status === 'verified' ? '✓' : wp.status === 'pending' ? '…' : wp.status === 'queued' ? '↑' : '';
  return L.divIcon({
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `<div data-testid="wp-marker-${wp.id}" data-status="${wp.status || 'none'}" style="width:${size}px;height:${size}px;border-radius:50%;background:${wp.color || '#9ca3af'};border:3px ${wp.status === 'pending' || wp.status === 'queued' ? 'dashed' : 'solid'} ${ring};box-shadow:0 2px 6px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:800;font-size:${size * 0.5}px;line-height:1;${wp.status === 'verified' ? 'opacity:.85' : ''}">${mark}</div>`,
  });
};

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length) map.fitBounds(L.latLngBounds(points), { padding: [30, 30], maxZoom: 12 });
  }, [map, points]);
  return null;
}

export default function PassportMap() {
  const { registrationId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [fromCache, setFromCache] = useState(null);
  const [active, setActive] = useState(new Set());
  const [hideDone, setHideDone] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [rankingOpen, setRankingOpen] = useState(false);
  const [view, setView] = useState('map');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/registrations/${registrationId}/passport`);
      setData(r.data);
      setFromCache(null);
      offlineQueue.cachePassport(registrationId, r.data);
    } catch (e) {
      const cached = await offlineQueue.getPassport(registrationId);
      if (cached && !e.response) {
        setData(cached.data);
        setFromCache(cached.cachedAt);
      } else {
        setError(e.response?.data?.detail || 'No se pudo cargar el pasaporte');
      }
    }
  }, [registrationId]);

  useEffect(() => { load(); }, [load]);

  const { queue, flushing, online, enqueue, flush, remove: removeQueued } = usePhotoQueue(registrationId, load);
  const queuedByWp = useMemo(() => Object.fromEntries(queue.map((q) => [q.waypointId, q])), [queue]);
  const waypoints = useMemo(() => (data ? data.waypoints.map((w) => (queuedByWp[w.id] && w.status !== 'verified' && w.status !== 'pending' ? { ...w, status: 'queued', queued: queuedByWp[w.id] } : w)) : []), [data, queuedByWp]);

  const visible = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return waypoints.filter((w) =>
      (w.waypoint_type === 'info' ? active.size === 0 : (active.size === 0 || active.has(w.category))) &&
      (!hideDone || w.status !== 'verified') &&
      (!q || w.name?.toLowerCase().includes(q) || w.province?.toLowerCase().includes(q))
    );
  }, [data, waypoints, active, hideDone, query]);

  const allPoints = useMemo(() => (data ? data.waypoints.filter((w) => w.waypoint_type !== 'info').map((w) => [w.latitude, w.longitude]) : []), [data]);
  const selected = waypoints.find((w) => w.id === selectedId) || null;

  const toggleCat = (c) => setActive((prev) => { const n = new Set(prev); n.has(c) ? n.delete(c) : n.add(c); return n; });

  if (error) {
    return <div className="min-h-[60vh] flex items-center justify-center p-6 text-center text-gray-700" data-testid="passport-error">{error}</div>;
  }
  if (!data) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-violet-600" /></div>;
  }

  return (
    <div className="flex flex-col bg-gray-50 h-[calc(100vh-144px)] md:h-[calc(100vh-64px)]" data-testid="passport-page">
      <div className="bg-white border-b border-gray-200 px-3 pt-3 pb-2 space-y-2 shadow-sm z-[1050]">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate('/my-registrations')} className="p-2 -ml-1 rounded-full hover:bg-gray-100" aria-label="Volver" data-testid="passport-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-violet-700 font-semibold flex items-center gap-1"><Stamp className="w-3.5 h-3.5" /> Mapa Pasaporte</p>
            <h1 className="text-base font-bold text-gray-900 truncate">{data.event.title}</h1>
          </div>
          <div className="text-right shrink-0" data-testid="passport-counter">
            <p className="text-2xl font-black text-violet-800 leading-none">{data.verified}<span className="text-sm font-medium text-gray-400"> / {data.total}</span></p>
            <p className="text-[11px] text-gray-500">{data.pending > 0 ? `${data.pending} en revisión` : 'acreditados'}</p>
          </div>
          <button type="button" onClick={() => setRankingOpen(true)} className="h-10 w-10 shrink-0 rounded-full bg-yellow-50 border border-yellow-300 flex items-center justify-center hover:bg-yellow-100" aria-label="Clasificación" data-testid="passport-ranking-btn">
            <Trophy className="w-5 h-5 text-yellow-600" />
          </button>
          {data.diploma_available && data.completed_challenges.length > 0 && (
            <button type="button" onClick={() => downloadDiploma(registrationId, data.completed_challenges[0])} className="h-10 w-10 shrink-0 rounded-full bg-amber-500 flex items-center justify-center hover:bg-amber-600" aria-label="Descargar diploma" title={`Diploma ${data.completed_challenges[0].name}`} data-testid="passport-diploma-btn">
              <Award className="w-5 h-5 text-white" />
            </button>
          )}
          {data.completed_challenges.length > 0 && (
            <button type="button" onClick={() => shareAchievement(registrationId, data.completed_challenges[0])} className="h-10 w-10 shrink-0 rounded-full bg-emerald-600 flex items-center justify-center hover:bg-emerald-700" aria-label="Compartir logro" title={`Compartir ${data.completed_challenges[0].name}`} data-testid="passport-share-btn">
              <Share2 className="w-5 h-5 text-white" />
            </button>
          )}
          <div className="flex rounded-full border border-gray-300 overflow-hidden shrink-0" data-testid="passport-view-toggle">
            <button type="button" onClick={() => setView('map')} className={`h-10 w-10 flex items-center justify-center ${view === 'map' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600'}`} aria-label="Mapa" data-testid="passport-view-map"><MapIcon className="w-5 h-5" /></button>
            <button type="button" onClick={() => setView('grid')} className={`h-10 w-10 flex items-center justify-center ${view === 'grid' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600'}`} aria-label="Sellos" data-testid="passport-view-grid"><LayoutGrid className="w-5 h-5" /></button>
          </div>
        </div>
        <VestigiaRanking eventId={data.event.id} open={rankingOpen} onOpenChange={setRankingOpen} />
        {(!online || fromCache) && (
          <p className="text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 flex items-center gap-1" data-testid="passport-offline-banner">
            <WifiOff className="w-3.5 h-3.5" /> {online ? 'Sin conexión con el servidor' : 'Sin cobertura'}: mostrando datos guardados{fromCache ? ` (${new Date(fromCache).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })})` : ''}. Las fotos se guardan en el móvil y se subirán solas.
          </p>
        )}
        {queue.length > 0 && (
          <div className="flex items-center gap-2 text-xs font-medium text-blue-900 bg-blue-50 border border-blue-200 rounded-md px-2 py-1" data-testid="passport-queue-banner">
            <CloudUpload className={`w-4 h-4 ${flushing ? 'animate-bounce' : ''}`} />
            <span className="flex-1" data-testid="passport-queue-count">{queue.length} foto{queue.length > 1 ? 's' : ''} guardada{queue.length > 1 ? 's' : ''} en el móvil, pendiente{queue.length > 1 ? 's' : ''} de subir</span>
            <button type="button" onClick={() => flush(false)} disabled={flushing || !online} className="h-8 px-3 rounded-full bg-blue-600 text-white font-semibold disabled:opacity-50" data-testid="passport-queue-flush-btn">
              {flushing ? 'Subiendo…' : 'Subir ahora'}
            </button>
          </div>
        )}
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar waypoint o provincia…"
            className="w-full h-10 rounded-full border border-gray-300 bg-gray-50 pl-9 pr-3 text-sm"
            data-testid="passport-search-input"
          />
        </div>
        <CategoryFilter
          categories={data.categories}
          active={active}
          onToggle={toggleCat}
          onAll={() => setActive(new Set())}
          hideDone={hideDone}
          onHideDone={() => setHideDone((v) => !v)}
        />
      </div>

      <div className="flex-1 relative min-h-0">
        {view === 'grid' ? (
          <PassportGrid waypoints={waypoints} categories={data.categories} stamps={data.category_stamps} onSelect={setSelectedId} />
        ) : (<>
        <div className="absolute top-2 right-2 z-[1000]"><MapSelector compact /></div>
        <div className="absolute bottom-2 left-2 z-[1000] bg-white/90 backdrop-blur rounded-md px-2 py-1 text-xs text-gray-700 shadow" data-testid="passport-visible-count">
          {visible.filter((w) => w.waypoint_type !== 'info').length} waypoints en el mapa
        </div>
        <MapContainer center={allPoints[0] || [40.4, -3.7]} zoom={6} style={{ height: '100%', width: '100%' }} className="z-0">
          <DynamicTileLayer />
          <FitBounds points={allPoints} />
          {visible.map((w) => (
            <Marker
              key={w.id}
              position={[w.latitude, w.longitude]}
              icon={markerIcon(w, w.id === selectedId, data.category_stamps?.[w.category])}
              eventHandlers={{ click: () => setSelectedId(w.id) }}
            />
          ))}
        </MapContainer>
        </>)}
        {selected && (
          <WaypointSheet wp={selected} registrationId={registrationId} stampUrl={data.category_stamps?.[selected.category]} onClose={() => setSelectedId(null)} onChanged={load} onQueue={enqueue} onUnqueue={removeQueued} online={online} />
        )}
      </div>
    </div>
  );
}
